import React, { useMemo } from 'react'
import {
  Box,
  useTokens,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
  Typography,
  Alert,
  Button,
  ListItem
} from '@barracuda-internal/bds-core-v2'
import { useFormatMessage } from 'lib/localization'
import SetupFlowStepsContentContainer from '../../../layout/SetupFlowStepsContentContainer'
import ContentTitleComponent from '../../../components/ContentTitleComponent'
import ErrorComponent from '../../../components/ErrorComponent'
import { useVerifyMsDomainLogic } from './useVerifyMsDomainLogic'

const BASE_I18N_KEY = 'ess_wizard.verify_domain'

const VerifyMsDomain = () => {
  const tokens = useTokens()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)

  const [state, eventHandlers] = useVerifyMsDomainLogic()

  return useMemo(
    () => (
      <SetupFlowStepsContentContainer>
        <ContentTitleComponent
          title={formatMessage('title')}
          subtitle={formatMessage(`${state.unavailableDomains.length === 0 ? 'support_subtitle' : 'subtitle'}`, {
            a: (txt: any): any => (
              <Link target="_blank" href="https://www.barracuda.com/support/contact">
                {txt}
              </Link>
            )
          })}
        />
        <ErrorComponent />
        {state.availableDomains.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              padding: tokens.aliasesSpaceContainerDefault,
              gap: tokens.aliasesSpaceContainerDefault,
              flexDirection: 'column'
            }}
          >
            {state.unavailableDomains.length > 0 && (
              <Typography
                sx={{
                  fontFamily: tokens.aliasesFontFamilyDefault,
                  fontSize: tokens.aliasesFontSizeH4,
                  fontWeight: tokens.aliasesFontWeightNeutral,
                  color: tokens.aliasesColorTextStaticEmphasis,
                  lineHeight: tokens.aliasesLineHeightH4
                }}
              >
                {formatMessage('available_domains')}
              </Typography>
            )}
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              value={state.msDomainName}
              onChange={eventHandlers.onSelectChange}
              name="radio-buttons-group"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'nowrap'
              }}
            >
              {state.availableDomains.map((name, index) => (
                <ListItem
                  key={name}
                  bdsType="staticSubtle"
                  bottomBorder={index !== state.availableDomains.length - 1}
                  bdsPadding="dense"
                  bdsSize="small"
                >
                  <FormControlLabel control={<Radio bdsSize="small" />} variant="toggle" label={name} value={name} />
                </ListItem>
              ))}
            </RadioGroup>
          </Box>
        )}

        {state.unavailableDomains.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              paddingLeft: tokens.aliasesSpaceContainerDefault,
              paddingRight: tokens.aliasesSpaceContainerDefault,
              paddingBottom: tokens.aliasesSpaceContainerDefault,
              gap: tokens.aliasesSpaceContainerDefault,
              flexDirection: 'column'
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: tokens.aliasesFontFamilyDefault,
                  fontSize: tokens.aliasesFontSizeH4,
                  fontWeight: tokens.aliasesFontWeightNeutral,
                  color: tokens.aliasesColorTextStaticEmphasis,
                  lineHeight: tokens.aliasesLineHeightH4
                }}
              >
                {formatMessage('unavailable_domains')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: tokens.aliasesFontFamilyDefault,
                  fontSize: tokens.aliasesFontSizeCaption,
                  fontWeight: tokens.aliasesFontWeightNeutral,
                  color: tokens.aliasesColorTextStaticSubtle,
                  lineHeight: tokens.aliasesLineHeightCaption
                }}
              >
                {formatMessage('unavailable_domains_subtitle')}
              </Typography>
            </Box>
            <Box>
              {state.unavailableDomains.map((domain, index) => (
                <ListItem
                  key={domain.domain}
                  bdsType="staticSubtle"
                  bottomBorder={index !== state.unavailableDomains.length - 1}
                  bdsPadding="dense"
                  bdsSize="small"
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography>{domain.domain}</Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography>{domain.account_name}</Typography>
                    <Typography
                      sx={{
                        fontFamily: tokens.aliasesFontFamilyDefault,
                        fontSize: tokens.aliasesFontSizeCaption,
                        fontWeight: tokens.aliasesFontWeightNeutral,
                        color: tokens.aliasesColorTextStaticSubtle,
                        lineHeight: tokens.aliasesLineHeightCaption
                      }}
                    >
                      {domain.portal_id}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </Box>
            <Box
              sx={{
                paddingTop: tokens.aliasesSpaceContainerDefault
              }}
            >
              <Alert
                bdsType="neutralInfo"
                bdsBoxShadowType="inline"
                action={
                  <Button
                    bdsType="interactiveSubtle"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() =>
                      window.open('https://www.barracuda.com/support/contact', '_blank', 'noopener,noreferrer')
                    }
                  >
                    {formatMessage('contact_support')}
                  </Button>
                }
              >
                {formatMessage('contact_info')}
              </Alert>
            </Box>
          </Box>
        )}
      </SetupFlowStepsContentContainer>
    ),
    [tokens, formatMessage, state, eventHandlers]
  )
}

export default VerifyMsDomain
